/**
 * El tema de la agencia (§16.3d): los colores con que PROTECT se presenta como el departamento.
 *
 * Las reglas de contraste son las del servidor (`AgencyBranding.ReadabilityProblem`); aquí se
 * repiten para avisar mientras se elige, no para decidir: quien decide es el servidor.
 */

/** El texto blanco del nombre y el título, sobre el color principal. */
export const TEXT_ON_PRIMARY = '#FFFFFF'
/** El texto del botón de ingreso, sobre el acento. */
export const TEXT_ON_ACCENT = '#04121F'

export interface ThemePreset {
  id: string
  primaryColor: string
  accentColor: string
}

/**
 * Temas listos. Todos pasan las reglas de contraste; la agencia puede partir de uno y ajustar.
 * Son combinaciones genéricas, no los colores de ningún departamento real.
 */
export const THEME_PRESETS: ThemePreset[] = [
  { id: 'protect', primaryColor: '#0A1628', accentColor: '#1EB6FF' },
  { id: 'navy', primaryColor: '#0B1F3A', accentColor: '#6FB1FF' },
  { id: 'sheriff', primaryColor: '#12281C', accentColor: '#D4AF37' },
  { id: 'tan', primaryColor: '#2B1D12', accentColor: '#E0B45C' },
  { id: 'graphite', primaryColor: '#1A1D21', accentColor: '#9ECBFF' },
  { id: 'burgundy', primaryColor: '#3A0F18', accentColor: '#F2C14E' },
]

export const isColor = (v: string) => /^#[0-9A-Fa-f]{6}$/.test(v)

function luminance(color: string): number {
  const n = parseInt(color.slice(1), 16)
  const canal = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * canal((n >> 16) & 0xff) + 0.7152 * canal((n >> 8) & 0xff) + 0.0722 * canal(n & 0xff)
}

/** El contraste WCAG 2 entre dos colores #RRGGBB, de 1 a 21. */
export function contrast(a: string, b: string): number {
  const [la, lb] = [luminance(a), luminance(b)]
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

export type ThemeProblem = 'format' | 'primaryLight' | 'accentDark' | 'accentBlends'

/** Por qué no se leería, en el mismo orden que el servidor; nulo si se lee. */
export function readabilityProblem(primaryColor: string, accentColor: string): ThemeProblem | null {
  if (!isColor(primaryColor) || !isColor(accentColor)) return 'format'
  if (contrast(primaryColor, TEXT_ON_PRIMARY) < 4.5) return 'primaryLight'
  if (contrast(accentColor, TEXT_ON_ACCENT) < 4.5) return 'accentDark'
  if (contrast(primaryColor, accentColor) < 3) return 'accentBlends'
  return null
}

/** Las iniciales del escudo neutro: las palabras que cuentan, hasta tres. */
export function initials(agencyName: string): string {
  return agencyName.split(/\s+/).filter((w) => /^[A-Za-zÁÉÍÓÚÑ]/.test(w) && w.length > 2)
    .slice(0, 3).map((w) => w[0]!.toUpperCase()).join('') || 'P'
}

/** El escudo pesa hasta 512 KB y es PNG, JPEG o WebP, como exige el servidor. */
export const MAX_BADGE_BYTES = 512 * 1024
export const BADGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']

// ------------------------------------------------------------------ el resto de la aplicación

/** Mezcla dos colores #RRGGBB: con `t` 0 queda `a`, con 1 queda `b`. */
export function mix(a: string, b: string, t: number): string {
  const [x, y] = [parseInt(a.slice(1), 16), parseInt(b.slice(1), 16)]
  const canal = (s: number) => Math.round(((x >> s) & 0xff) * (1 - t) + ((y >> s) & 0xff) * t)
  return '#' + [16, 8, 0].map((s) => canal(s).toString(16).padStart(2, '0')).join('').toUpperCase()
}

/**
 * El color, corrido hacia `hacia` lo justo para que contraste `minimo` contra `fondo`. Una agencia
 * puede elegir un principal tan claro como la regla del ingreso permita: de fondo de toda la
 * aplicación, el texto gris dejaría de leerse. Aquí se oscurece (o aclara) lo que haga falta.
 */
export function ensureContrast(color: string, fondo: string, minimo: number, hacia: string): string {
  for (let t = 0; t <= 1.0001; t += 0.02) {
    const c = mix(color, hacia, Math.min(t, 1))
    if (contrast(c, fondo) >= minimo) return c
  }
  return hacia
}

/** Los textos de la aplicación, que no cambian con la agencia (§16.1: el significado no se tiñe). */
const TEXT_DARK_THEME = '#E3E6EB'
const MUTED_DARK_THEME = '#9AA3AF'
const BG_LIGHT_THEME = '#F6F7F9'
const SURFACE2_LIGHT_THEME = '#EEF1F5'

/**
 * Los tokens de la aplicación con los colores de la agencia (§16.3d), para el tema oscuro o claro.
 *
 * Oscuro, como los diseños de Jesús: el fondo y los paneles salen del principal de la agencia, y
 * botones, títulos y enlaces del acento. Claro: las superficies siguen claras y lo de la marca va
 * con el principal. Los colores de estado (bien, aviso, peligro) no se tocan: su significado no
 * puede depender de la agencia.
 */
export function appPalette(primaryColor: string, accentColor: string, dark: boolean): Record<string, string> {
  const principal = isColor(primaryColor) ? primaryColor.toUpperCase() : '#0A1628'
  const acento = isColor(accentColor) ? accentColor.toUpperCase() : '#1EB6FF'
  if (dark) {
    // El fondo, lo bastante oscuro para que el texto gris de los paneles se lea.
    const bg = ensureContrast(principal, TEXT_DARK_THEME, 12, '#000000')
    const surface = mix(bg, '#FFFFFF', 0.04)
    const surface2 = mix(bg, '#FFFFFF', 0.08)
    // El acento como texto (títulos, enlaces) sobre el panel más claro.
    const primary = ensureContrast(acento, surface2, 4.5, '#FFFFFF')
    return {
      '--bg': bg,
      '--surface': surface,
      '--surface-2': surface2,
      '--border': mix(bg, acento, 0.22),
      '--primary': primary,
      '--on-primary': TEXT_ON_ACCENT,
      '--info': primary,
    }
  }
  const primary = ensureContrast(principal, SURFACE2_LIGHT_THEME, 4.5, '#000000')
  return { '--primary': primary, '--on-primary': TEXT_ON_PRIMARY, '--info': primary }
}

/** Para las pruebas y para quien quiera comprobar: los pares que tienen que leerse. */
export const PALETTE_TEXTS = { TEXT_DARK_THEME, MUTED_DARK_THEME, BG_LIGHT_THEME, SURFACE2_LIGHT_THEME }
